import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft, faXmark } from '@fortawesome/free-solid-svg-icons';
import {
    loadRecentSearches, peekRecentSearches, removeRecentSearch, saveRecentSearch, subscribeRecentSearches
} from '../utils/recentSearches';
import '../styles/recentSearches.css';

/**
 * Recent searches for one search box.
 *
 * When the box is focused and empty, the person's last few searches drop down
 * under it; picking one fills the box (which runs the search) and moves it to
 * the top. A search is saved when they press Enter, or when the page calls
 * `save` because they opened one of the results.
 *
 *   const recent = useRecentSearches('admin.tasks', { value: searchTerm, onPick: setSearchTerm });
 *   <input {...recent.inputProps} value={searchTerm} onChange={...} />
 *   {recent.menu}
 *   ...onClick={() => { recent.save(searchTerm); openTask(task); }}
 *
 * Handlers and a ref the input already has go in the options, so both run.
 * The list is drawn in a portal at the input's position, so it shows above
 * cards, tables and panels that clip their content.
 */
export function useRecentSearches(scope, { value = '', onPick, onKeyDown, onFocus, onBlur, inputRef } = {}) {
    const [terms, setTerms] = useState(() => peekRecentSearches(scope));
    const [focused, setFocused] = useState(false);
    const [active, setActive] = useState(-1);
    const [box, setBox] = useState(null);
    const input = useRef(null);
    const listId = useId();

    useEffect(() => {
        setTerms(peekRecentSearches(scope));
        return subscribeRecentSearches(scope, setTerms);
    }, [scope]);

    const open = focused && !String(value || '').trim() && terms.length > 0;

    useEffect(() => { if (!open) setActive(-1); }, [open]);

    // Follows the input while the page scrolls or resizes.
    useLayoutEffect(() => {
        if (!open) return undefined;
        const place = () => {
            const rect = input.current?.getBoundingClientRect();
            if (!rect || !rect.width) { setBox(null); return; }
            const width = Math.min(Math.max(rect.width, 240), window.innerWidth - 16);
            setBox({ top: rect.bottom + 4, left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)), width });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [open]);

    const save = useCallback((term) => saveRecentSearch(scope, term ?? value), [scope, value]);

    const pick = (term) => {
        onPick?.(term);
        saveRecentSearch(scope, term);
        setActive(-1);
    };

    const setRef = useCallback((node) => {
        input.current = node;
        if (typeof inputRef === 'function') inputRef(node);
        else if (inputRef) inputRef.current = node;
    }, [inputRef]);

    const inputProps = {
        ref: setRef,
        autoComplete: 'off',
        'aria-expanded': open,
        'aria-controls': open ? listId : undefined,
        'aria-activedescendant': open && active >= 0 ? `${listId}-${active}` : undefined,
        onFocus: (e) => {
            setFocused(true);
            loadRecentSearches(scope);
            onFocus?.(e);
        },
        onBlur: (e) => {
            setFocused(false);
            onBlur?.(e);
        },
        onKeyDown: (e) => {
            if (open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                e.preventDefault();
                const n = terms.length;
                setActive((i) => (e.key === 'ArrowDown' ? (i + 1) % n : (i <= 0 ? n - 1 : i - 1)));
                return;
            }
            if (e.key === 'Enter' && !e.nativeEvent?.isComposing) {
                if (open && active >= 0 && terms[active]) {
                    e.preventDefault();
                    pick(terms[active]);
                    return;
                }
                const text = e.currentTarget?.value ?? value;
                if (String(text || '').trim()) saveRecentSearch(scope, text);
            }
            if (e.key === 'Escape' && open) setFocused(false);
            onKeyDown?.(e);
        }
    };

    const menu = open && box ? createPortal(
        <div
            className="recent-searches"
            style={{ top: box.top, left: box.left, width: box.width }}
            // Keeps focus in the search box, so a click does not close the list first.
            onMouseDown={(e) => e.preventDefault()}
        >
            <div className="recent-searches-head">
                <span>Recent searches</span>
                <button type="button" className="recent-searches-clear" onClick={() => removeRecentSearch(scope)}>Clear</button>
            </div>
            <ul id={listId} role="listbox" aria-label="Recent searches">
                {terms.map((term, i) => (
                    <li
                        key={term}
                        id={`${listId}-${i}`}
                        role="option"
                        aria-selected={i === active}
                        className={`recent-searches-item${i === active ? ' is-active' : ''}`}
                        onClick={() => pick(term)}
                        onMouseEnter={() => setActive(i)}
                    >
                        <FontAwesomeIcon icon={faClockRotateLeft} className="recent-searches-icon" />
                        <span className="recent-searches-term">{term}</span>
                        <button
                            type="button"
                            className="recent-searches-remove"
                            aria-label={`Remove "${term}" from recent searches`}
                            title="Remove"
                            onClick={(e) => { e.stopPropagation(); removeRecentSearch(scope, term); }}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </li>
                ))}
            </ul>
        </div>,
        document.body
    ) : null;

    return { inputProps, menu, save };
}

export default useRecentSearches;
